"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { fetchProducts } from "@/store/slices/homeSlice";
import { clearFilters, setSelectedBrand } from "@/store/slices/stepSlice";
import { trackEvent } from "@/lib/gtag";
import { Manufacturer } from "@/types/home";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

const MobileBrandSelection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { manufacturers } = useAppSelector((state) => state.home);
  const { selectedBrand } = useAppSelector((state) => state.steps);
  const [selectedTab, setSelectedTab] = useState<"domestic" | "premium">("domestic");
  const [loadingBrand, setLoadingBrand] = useState<string | null>(null);

  const mergeOthers = (category: "domestic" | "premium"): Manufacturer[] => [
    ...(manufacturers?.others ?? []),
    ...(manufacturers?.[category] ?? []),
  ];

  useEffect(() => {
    const brands = mergeOthers("domestic");
    if (brands.length && !selectedBrand) {
      setLoadingBrand("MASTER");
      dispatch(setSelectedBrand("MASTER"));
      dispatch(fetchProducts("MASTER")).finally(() => setLoadingBrand(null));
    }
  }, [dispatch, manufacturers]);

  const handleSelectBrand = (brand: string | null) => {
    setLoadingBrand(brand);
    dispatch(clearFilters());
    dispatch(setSelectedBrand(brand || "MASTER"));
    dispatch(fetchProducts(brand || "MASTER")).finally(() => setLoadingBrand(null));
    trackEvent({
      action: "brand_click",
      category: "brand_selection",
      label: `Brand Selected: ${brand || "MASTER"}`,
    });
  };

  return (
    <div className="w-full px-4 pt-2 pb-0">
      {/* Tabs */}
      <div className="flex space-x-6 border-b border-gray-200 mb-4 overflow-x-auto">
        {["domestic", "premium"].map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab as "domestic" | "premium")}
            className={cn(
              "pb-2 text-sm font-semibold capitalize border-b-2 transition-all duration-300",
              selectedTab === tab
                ? "border-green-600 text-green-700"
                : "border-transparent text-gray-500 hover:text-black-1"
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Brands Grid (4 per row) */}
      <div className="grid grid-cols-4 gap-x-2 gap-y-3">
        {mergeOthers(selectedTab).map((brand) => {
          const isSelected =
            selectedBrand?.toLowerCase() === brand.manufacture_name.toLowerCase();

          return (
            <Button
              key={brand.manufacture_code}
              variant="ghost"
              onClick={() => handleSelectBrand(brand.manufacture_name)}
              className={cn(
                "h-[35px] min-w-0 w-full p-1 rounded-md flex items-center justify-center bg-white",
                isSelected
                  ? "border-2 border-gray-800"
                  : "border border-gray-300 hover:border-gray-500"
              )}
            >
              {loadingBrand === brand.manufacture_name ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
              ) : (
                <Image
                  unoptimized
                  quality={60}
                  alt={brand.manufacture_name}
                  src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_MANUFACTURE_IMAGE/${brand.manufacture_images}`}
                  width={65}
                  height={22}
                  className="object-contain w-[60px] h-[30px]"
                />
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default MobileBrandSelection;
